import {
  BaseClient,
  BaseContactClientRequestAssignee,
  BaseFile,
  BaseJobServiceLine,
  ClientApiService,
  ClientRequestApiService,
  ClientRequestDetails,
  ClientRequestGroupJobModel,
  ClientRequestGroupModel,
  ClientRequestGroupType,
  ClientRequestMetadataModel,
  ClientRequestModel,
  ContactApiService,
  DeliverableModel,
  DocumentRequestMetadataModel,
  FileApiService,
  JobApiService,
  QuestionnaireResponse,
  RequestDocument,
  RequestItem,
  RequestSection,
  RequestSubmissionFile,
  StaffApiService,
  StaffEntity,
} from '@aiwyn/api';
import { AuthService, AuthStateAdaptorService, ContactUser } from '@aiwyn/auth';
import {
  BaseContact,
  FileModel,
  flattenIdentitiesToRequest,
  getLoadableArrayLoadingState,
  Identity,
  ignoreFalsy,
  isEmpty,
  isLoaded,
  Loadable,
  LoadableArrayLoadStrategy,
  LoadableHelper,
  LoadableStatus,
  Logger,
  removeDuplicates,
  replaceArrayItem,
  ResetState,
  setLoadableArrayInState,
} from '@aiwyn/shared';
import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { patch, updateItem } from '@ngxs/store/operators';
import { combineLatest, Observable, of, throwError } from 'rxjs';
import { map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { ClientRequestGroupIds, CommonClientRequestGroupIdentityToModelMap, TypedIdentity } from '../models';
import { AssigneeIdentityType, ClientIdentityType, ClientRequestIdentityHelper, DeliverableIdentityType } from '../utility';
import {
  AddClientRequestMetadataFiles,
  GetAllClientRequestGroups,
  GetAllClientRequests,
  GetAllDeliverables,
  GetAssignees,
  GetClientRequestDetails,
  GetClientRequestDetailsSuccess,
  GetClientRequestMetadata,
  GetClientRequests,
  GetClients,
  GetFiles,
  GetQuestionnaireResponses,
  GetServiceLines,
  GetStaff,
  InsertAnswersIntoRequestItems,
  InsertCategorizedFilesIntoClientRequest,
  InsertUncategorizedFilesIntoClientRequest,
  MarkDeliverableFiles,
  PatchClientRequestState,
  RemoveClientRequestFiles,
  RemoveClientRequestFilesSuccess,
  RemoveSecureLinkUserForRequestItem,
  SetClientRequestItemNotApplicableSuccess,
  SetSecureLinkUserForRequestItem,
  UnCategorizeFilesFromClientRequestDocuments,
  UpdateDeliverablesFromSubscription,
} from './common-client-requests.actions';
import { CommonClientRequestsStateModel, DEFAULT_COMMON_CLIENT_REQUESTS_STATE } from './common-client-requests.model';
import { isNil } from 'lodash';

export const COMMON_CLIENT_REQUESTS_STATE_NAME = 'commonClientRequests';

@Injectable({ providedIn: 'root' })
@State<CommonClientRequestsStateModel>({
  name: COMMON_CLIENT_REQUESTS_STATE_NAME,
  defaults: DEFAULT_COMMON_CLIENT_REQUESTS_STATE,
})
export class CommonClientRequestsState {
  //#region Selectors

  //#region Client Request Groups

  @Selector()
  static getAllClientRequestGroupsLoadables(state: CommonClientRequestsStateModel): Loadable<ClientRequestGroupModel>[] {
    return state?.groups;
  }

  @Selector([CommonClientRequestsState.getAllClientRequestGroupsLoadables])
  static getAllClientRequestGroupsStatuses(groups: Loadable<ClientRequestGroupModel>[]): LoadableStatus[] {
    return LoadableHelper.getStatuses(groups);
  }

  @Selector([CommonClientRequestsState.getAllClientRequestGroupsStatuses])
  static getAllClientRequestGroupsStatus(statuses: LoadableStatus[]): LoadableStatus {
    return LoadableHelper.coalesceStatus(statuses);
  }

  @Selector([CommonClientRequestsState.getAllClientRequestGroupsLoadables])
  static getAllClientRequestGroups(groups: Loadable<ClientRequestGroupModel>[]): ClientRequestGroupModel[] {
    return LoadableHelper.getData(groups).filter(Boolean);
  }

  static getClientRequestGroupsLoadables(
    requestGroupIds: ClientRequestGroupIds,
  ): (groups: Loadable<ClientRequestGroupModel>[]) => Loadable<ClientRequestGroupModel>[] {
    return createSelector(
      [CommonClientRequestsState.getAllClientRequestGroupsLoadables],
      groups => (groups ?? []).filter(({ data }) => {
        switch (data.type) {
          case ClientRequestGroupType.JOB:
            return requestGroupIds.jobIds.includes(data.job.id);
          default:
            return requestGroupIds.ungrouped;
        }
      }),
    );
  }

  static getClientRequestGroupsStatuses(
    requestGroupIds: ClientRequestGroupIds,
  ): (groups: Loadable<ClientRequestGroupModel>[]) => LoadableStatus[] {
    return createSelector(
      [CommonClientRequestsState.getClientRequestGroupsLoadables(requestGroupIds)],
      groups => LoadableHelper.getStatuses(groups),
    );
  }

  static getClientRequestGroupsStatus(
    requestGroupIds: ClientRequestGroupIds,
  ): (statuses: LoadableStatus[]) => LoadableStatus {
    return createSelector(
      [CommonClientRequestsState.getClientRequestGroupsStatuses(requestGroupIds)],
      statuses => LoadableHelper.coalesceStatus(statuses),
    );
  }

  static getClientRequestGroups(
    requestGroupIds: ClientRequestGroupIds,
  ): (groups: Loadable<ClientRequestGroupModel>[]) => ClientRequestGroupModel[] {
    return createSelector(
      [CommonClientRequestsState.getClientRequestGroupsLoadables(requestGroupIds)],
      groups => LoadableHelper.getData(groups),
    );
  }

  // may not keep the Model generic type
  static getClientRequestGroupLoadable<
    Identity extends TypedIdentity<ClientRequestGroupType> = TypedIdentity<ClientRequestGroupType>,
    Model extends CommonClientRequestGroupIdentityToModelMap<Identity> = CommonClientRequestGroupIdentityToModelMap<Identity>,
  >(groupIdentity: Identity): (groups: Loadable<Model>[]) => Loadable<Model> {
    return createSelector(
      [CommonClientRequestsState.getAllClientRequestGroupsLoadables],
      groups => groups.find(({ identity }) => identity === groupIdentity),
    );
  }

  //#endregion

  //#region Client Requests

  @Selector()
  static getAllClientRequestsLoadables({ requests }: CommonClientRequestsStateModel): Loadable<ClientRequestModel>[] {
    return requests;
  }

  @Selector([CommonClientRequestsState.getAllClientRequestsLoadables])
  static getAllClientRequestsStatuses(requests: Loadable<ClientRequestModel>[]): LoadableStatus[] {
    return LoadableHelper.getStatuses(requests);
  }

  @Selector([CommonClientRequestsState.getAllClientRequestsStatuses])
  static getAllClientRequestsStatus(statuses: LoadableStatus[]): LoadableStatus {
    return LoadableHelper.coalesceStatus(statuses);
  }

  @Selector([CommonClientRequestsState.getAllClientRequestsLoadables])
  static getAllClientRequests(requests: Loadable<ClientRequestModel>[]): ClientRequestModel[] {
    return LoadableHelper.getData(requests).filter(Boolean);
  }

  static getClientRequestsLoadables(requestIds: number[]): (requests: Loadable<ClientRequestModel>[]) => Loadable<ClientRequestModel>[] {
    return createSelector(
      [CommonClientRequestsState.getAllClientRequestsLoadables],
      requests => requests.filter(({ data }) => requestIds.includes(data.id)),
    );
  }

  static getClientRequestsStatuses(requestIds: number[]): (requests: Loadable<ClientRequestModel>[]) => LoadableStatus[] {
    return createSelector(
      [CommonClientRequestsState.getClientRequestsLoadables(requestIds)],
      requests => LoadableHelper.getStatuses(requests),
    );
  }

  static getClientRequestsStatus(requestIds: number[]): (statuses: LoadableStatus[]) => LoadableStatus {
    return createSelector(
      [CommonClientRequestsState.getClientRequestsStatuses(requestIds)],
      statuses => LoadableHelper.coalesceStatus(statuses),
    );
  }

  static getClientRequests(requestIds: number[]): (requests: Loadable<ClientRequestModel>[]) => ClientRequestModel[] {
    return createSelector(
      [CommonClientRequestsState.getClientRequestsLoadables(requestIds)],
      requests => LoadableHelper.getData(requests),
    );
  }

  static getClientRequestLoadablesInGroup(
    group: Loadable<ClientRequestGroupModel>,
  ): (requests: Loadable<ClientRequestModel>[]) => Loadable<ClientRequestModel>[] {
    const groupIdentity: TypedIdentity<ClientRequestGroupType> = group.identity as TypedIdentity<ClientRequestGroupType>
      || ClientRequestIdentityHelper.getGroupIdentity(group.data);

    return createSelector(
      [CommonClientRequestsState.getAllClientRequestsLoadables],
      requests => requests.filter(({ data }) => CommonClientRequestsState.doesClientRequestBelongToGroup(data, groupIdentity)),
    );
  }

  static doesClientRequestBelongToGroup(request: ClientRequestModel, groupIdentity: TypedIdentity<ClientRequestGroupType>): boolean {
    if (request == undefined) {
      return false;
    }

    const parsedGroupIdentity = ClientRequestIdentityHelper.parseGroupIdentity(groupIdentity);

    return parsedGroupIdentity.type === ClientRequestGroupType.JOB && request.jobId === parsedGroupIdentity.id;
  }

  static getClientRequestLoadable(requestId: number): (requests: Loadable<ClientRequestModel>[]) => Loadable<ClientRequestModel> {
    return createSelector(
      [CommonClientRequestsState.getAllClientRequestsLoadables],
      requests => CommonClientRequestsState.getClientRequestLoadableHelper(requests, requestId),
    );
  }

  static getClientRequestLoadableHelper(requests: Loadable<ClientRequestModel>[], requestId: number): Loadable<ClientRequestModel> {
    return requests.find(({ data }) => data?.id === requestId) || { status: LoadableStatus.ERROR };
  }

  static getClientRequestStatus(requestId: number): (requests: Loadable<ClientRequestModel>) => LoadableStatus {
    return createSelector(
      [CommonClientRequestsState.getClientRequestLoadable(requestId)],
      loadable => loadable?.status,
    );
  }

  static getClientRequest(requestId: number): (requests: Loadable<ClientRequestModel>) => ClientRequestModel {
    return createSelector(
      [CommonClientRequestsState.getClientRequestLoadable(requestId)],
      loadable => loadable?.data,
    );
  }

  //#endregion

  //#region Details

  @Selector()
  static getAllClientRequestDetailsLoadables({ details }: CommonClientRequestsStateModel): Loadable<ClientRequestDetails>[] {
    return details;
  }

  @Selector([CommonClientRequestsState.getAllClientRequestDetailsLoadables])
  static getAllClientRequestDetailsStatuses(details: Loadable<ClientRequestDetails>[]): LoadableStatus[] {
    return LoadableHelper.getStatuses(details);
  }

  @Selector([CommonClientRequestsState.getAllClientRequestDetailsStatuses])
  static getAllClientRequestDetailsStatus(statuses: LoadableStatus[]): LoadableStatus {
    return LoadableHelper.coalesceStatus(statuses);
  }

  @Selector([CommonClientRequestsState.getAllClientRequestDetailsLoadables])
  static getAllClientRequestDetails(details: Loadable<ClientRequestDetails>[]): ClientRequestDetails[] {
    return LoadableHelper.getData(details).filter(Boolean);
  }

  static getClientRequestDetailsLoadable(
    clientRequestId: Identity,
  ): (details: Loadable<ClientRequestDetails>[]) => Loadable<ClientRequestDetails> {
    return createSelector(
      [CommonClientRequestsState.getAllClientRequestDetailsLoadables],
      details => CommonClientRequestsState.getClientRequestDetailsLoadableHelper(details, clientRequestId),
    );
  }

  static getClientRequestDetailsLoadableHelper(
    details: Loadable<ClientRequestDetails>[],
    clientRequestId: Identity,
  ): Loadable<ClientRequestDetails> {
    return (details ?? []).find(
      typeof clientRequestId === 'number'
        ? ({ data }) => data?.clientRequestId === clientRequestId
        : ({ identity }) => identity === clientRequestId,
    );
  }

  static getClientRequestDetailsStatus(
    clientRequestId: Identity,
  ): (details: Loadable<ClientRequestDetails>) => LoadableStatus {
    return createSelector(
      [CommonClientRequestsState.getClientRequestDetailsLoadable(clientRequestId)],
      loadable => loadable?.status,
    );
  }

  static getClientRequestDetails(
    clientRequestId: Identity,
  ): (details: Loadable<ClientRequestDetails>) => ClientRequestDetails {
    return createSelector(
      [CommonClientRequestsState.getClientRequestDetailsLoadable(clientRequestId)],
      loadable => loadable?.data,
    );
  }

  static getClientRequestItemById(itemId: number): (details: Loadable<ClientRequestDetails>[]) => RequestItem | undefined {
    return createSelector(
      [CommonClientRequestsState.getAllClientRequestDetailsLoadables],
      (details) => details.reduce(
        (foundItem: RequestItem | undefined, detailLoadable: Loadable<ClientRequestDetails>) => {
          if (foundItem) {
            return foundItem;
          }

          const detail = detailLoadable.data;
          if (!detail) {
            return foundItem;
          }

          return detail.sections
            .flatMap((section: RequestSection) => section.items)
            .find((item: RequestItem) => item.id === itemId) ?? foundItem;
        },
        undefined as RequestItem | undefined,
      ),
    );
  }

  //#endregion

  //#region Metadata

  @Selector()
  static getAllClientRequestMetadataLoadables({ metadata }: CommonClientRequestsStateModel): Loadable<ClientRequestMetadataModel>[] {
    return metadata;
  }

  @Selector([CommonClientRequestsState.getAllClientRequestMetadataLoadables])
  static getAllClientRequestMetadataStatuses(metadata: Loadable<ClientRequestMetadataModel>[]): LoadableStatus[] {
    return LoadableHelper.getStatuses(metadata);
  }

  @Selector([CommonClientRequestsState.getAllClientRequestMetadataStatuses])
  static getAllClientRequestMetadataStatus(statuses: LoadableStatus[]): LoadableStatus {
    return LoadableHelper.coalesceStatus(statuses);
  }

  @Selector([CommonClientRequestsState.getAllClientRequestMetadataLoadables])
  static getAllClientRequestMetadata(metadata: Loadable<ClientRequestMetadataModel>[]): ClientRequestMetadataModel[] {
    return LoadableHelper.getData(metadata).filter(Boolean);
  }

  static getClientRequestMetadataLoadable(
    clientRequestId: Identity,
  ): (metadata: Loadable<ClientRequestMetadataModel>[]) => Loadable<ClientRequestMetadataModel> {
    return createSelector(
      [CommonClientRequestsState.getAllClientRequestMetadataLoadables],
      metadata => CommonClientRequestsState.getClientRequestMetadataLoadableHelper(metadata, clientRequestId),
    );
  }

  static getClientRequestMetadataLoadableHelper(
    metadata: Loadable<ClientRequestMetadataModel>[],
    clientRequestId: Identity,
  ): Loadable<ClientRequestMetadataModel> {
    return (metadata ?? []).find(
      typeof clientRequestId === 'number'
        ? ({ data }) => data?.clientRequestId === clientRequestId
        : ({ identity }) => identity === clientRequestId,
    );
  }

  static getClientRequestMetadataStatus(
    clientRequestId: Identity,
  ): (metadata: Loadable<ClientRequestMetadataModel>) => LoadableStatus {
    return createSelector(
      [CommonClientRequestsState.getClientRequestMetadataLoadable(clientRequestId)],
      loadable => loadable?.status,
    );
  }

  static getClientRequestMetadata(
    clientRequestId: Identity,
  ): (metadata: Loadable<ClientRequestMetadataModel>) => ClientRequestMetadataModel {
    return createSelector(
      [CommonClientRequestsState.getClientRequestMetadataLoadable(clientRequestId)],
      loadable => loadable?.data,
    );
  }

  //#endregion

  //#region Questionnaire Responses

  @Selector()
  static getQuestionnaireResponsesLoadable({ questionnaireResponses }: CommonClientRequestsStateModel): Loadable<QuestionnaireResponse[]> {
    return questionnaireResponses;
  }

  @Selector([CommonClientRequestsState.getQuestionnaireResponsesLoadable])
  static getQuestionnaireResponsesStatus(questionnaireResponsesLoadable: Loadable<QuestionnaireResponse[]>): LoadableStatus {
    return questionnaireResponsesLoadable?.status ?? LoadableStatus.DEFAULT;
  }

  @Selector([CommonClientRequestsState.getQuestionnaireResponsesLoadable])
  static getAllQuestionnaireResponses(questionnaireResponsesLoadable: Loadable<QuestionnaireResponse[]>): QuestionnaireResponse[] {
    return questionnaireResponsesLoadable?.data ?? [];
  }

  //#endregion

  //#region Clients

  @Selector()
  static getAllClientsLoadables({ clients }: CommonClientRequestsStateModel): Loadable<BaseClient>[] {
    return clients;
  }

  @Selector([CommonClientRequestsState.getAllClientsLoadables])
  static getAllClientsStatuses(clients: Loadable<BaseClient>[]): LoadableStatus[] {
    return LoadableHelper.getStatuses(clients);
  }

  @Selector([CommonClientRequestsState.getAllClientsStatuses])
  static getAllClientsStatus(statuses: LoadableStatus[]): LoadableStatus {
    return LoadableHelper.coalesceStatus(statuses);
  }

  @Selector([CommonClientRequestsState.getAllClientsLoadables])
  static getAllClients(clients: Loadable<BaseClient>[]): BaseClient[] {
    return LoadableHelper.getData(clients).filter(Boolean);
  }

  static getClientLoadables(clientIds: number[]): (clients: Loadable<BaseClient>[]) => Loadable<BaseClient>[] {
    return createSelector(
      [CommonClientRequestsState.getAllClientsLoadables],
      clients => clients.filter(({ data }) => clientIds.includes(data.id)),
    );
  }

  static getClientsStatuses(clientIds: number[]): (clients: Loadable<BaseClient>[]) => LoadableStatus[] {
    return createSelector(
      [CommonClientRequestsState.getClientLoadables(clientIds)],
      clients => clients.map(({ status }) => status),
    );
  }

  static getClients(clientIds: number[]): (clients: Loadable<BaseClient>[]) => BaseClient[] {
    return createSelector(
      [CommonClientRequestsState.getClientLoadables(clientIds)],
      clients => LoadableHelper.getData(clients),
    );
  }

  static getClientLoadable(
    clientIdentity: TypedIdentity<ClientIdentityType>,
  ): (clients: Loadable<BaseClient>[]) => Loadable<BaseClient> {
    return createSelector(
      [CommonClientRequestsState.getAllClientsLoadables],
      clients => clients.find(({ identity }) => identity === clientIdentity),
    );
  }

  static getClientStatus(
    clientIdentity: TypedIdentity<ClientIdentityType>,
  ): (loadable: Loadable<BaseClient>) => LoadableStatus {
    return createSelector(
      [CommonClientRequestsState.getClientLoadable(clientIdentity)],
      loadable => loadable?.status,
    );
  }

  static getClient(
    clientIdentity: TypedIdentity<ClientIdentityType>,
  ): (loadable: Loadable<BaseClient>) => BaseClient {
    return createSelector(
      [CommonClientRequestsState.getClientLoadable(clientIdentity)],
      loadable => loadable?.data,
    );
  }

  //#endregion

  //#region Assignees

  @Selector()
  static getAllAssigneeLoadables({ assignees }: CommonClientRequestsStateModel): Loadable<BaseContact>[] {
    return assignees;
  }

  @Selector([CommonClientRequestsState.getAllAssigneeLoadables])
  static getAllAssigneesStatuses(assigneeLoadables: Loadable<BaseContact>[]): LoadableStatus[] {
    return LoadableHelper.getStatuses(assigneeLoadables);
  }

  @Selector([CommonClientRequestsState.getAllAssigneesStatuses])
  static getAllAssigneesStatus(statuses: LoadableStatus[]): LoadableStatus {
    return LoadableHelper.coalesceStatus(statuses);
  }

  @Selector([CommonClientRequestsState.getAllAssigneeLoadables])
  static getAllAssignees(assigneeLoadables: Loadable<BaseContact>[]): BaseContact[] {
    return LoadableHelper.getData(assigneeLoadables).filter(Boolean);
  }

  static getAssigneesLoadables(
    assigneeIdentities: TypedIdentity<AssigneeIdentityType>[],
  ): (assignees: Loadable<BaseContact>[]) => Loadable<BaseContact>[] {
    return createSelector(
      [CommonClientRequestsState.getAllAssigneeLoadables],
      assignees => assignees.filter(({ identity }) => assigneeIdentities.includes(identity as TypedIdentity<AssigneeIdentityType>)),
    );
  }

  static getAssigneesStatuses(
    contactIdentities: TypedIdentity<AssigneeIdentityType>[],
  ): (assignees: Loadable<BaseContact>[]) => LoadableStatus[] {
    return createSelector(
      [CommonClientRequestsState.getAssigneesLoadables(contactIdentities)],
      assignees => assignees.map(({ status }) => status),
    );
  }

  static getAssignees(
    contactIdentities: TypedIdentity<AssigneeIdentityType>[],
  ): (assignees: Loadable<BaseContact>[]) => BaseContact[] {
    return createSelector(
      [CommonClientRequestsState.getAssigneesLoadables(contactIdentities)],
      assignees => LoadableHelper.getData(assignees),
    );
  }

  static getAssigneeLoadable(
    assigneeIdentity: TypedIdentity<AssigneeIdentityType>,
  ): (assignees: Loadable<BaseContact>[]) => Loadable<BaseContact> {
    return createSelector(
      [CommonClientRequestsState.getAllAssigneeLoadables],
      assignees => assignees.find(({ identity }) => identity === assigneeIdentity),
    );
  }

  static getAssigneeLoadableById(
    id: number,
  ): (assignees: Loadable<BaseContact>[]) => Loadable<BaseContact> {
    return createSelector(
      [CommonClientRequestsState.getAllAssigneeLoadables],
      assignees => assignees.find((assignee) => assignee.data.id === id),
    );
  }

  static getAssigneeStatus(
    assigneeIdentity: TypedIdentity<AssigneeIdentityType>,
  ): (loadable: Loadable<BaseContact>) => LoadableStatus {
    return createSelector(
      [CommonClientRequestsState.getAssigneeLoadable(assigneeIdentity)],
      loadable => loadable?.status,
    );
  }

  static getAssignee(
    assigneeIdentity: TypedIdentity<AssigneeIdentityType>,
  ): (loadable: Loadable<BaseContact>) => BaseContact {
    return createSelector(
      [CommonClientRequestsState.getAssigneeLoadable(assigneeIdentity)],
      loadable => loadable?.data,
    );
  }

  //#endregion

  //#region Staff

  @Selector()
  static getAllStaffLoadables({ staff }: CommonClientRequestsStateModel): Loadable<StaffEntity>[] {
    return staff;
  }

  @Selector([CommonClientRequestsState.getAllStaffLoadables])
  static getAllStaffStatuses(staffLoadables: Loadable<StaffEntity>[]): LoadableStatus[] {
    return LoadableHelper.getStatuses(staffLoadables);
  }

  @Selector([CommonClientRequestsState.getAllStaffStatuses])
  static getAllStaffStatus(statuses: LoadableStatus[]): LoadableStatus {
    return LoadableHelper.coalesceStatus(statuses);
  }

  @Selector([CommonClientRequestsState.getAllStaffLoadables])
  static getAllStaff(staffLoadables: Loadable<StaffEntity>[]): StaffEntity[] {
    return LoadableHelper.getData(staffLoadables).filter(Boolean);
  }

  static getStaffLoadables(staffIds: number[]): (staff: Loadable<StaffEntity>[]) => Loadable<StaffEntity>[] {
    return createSelector(
      [CommonClientRequestsState.getAllStaffLoadables],
      staff => staff.filter(({ data }) => staffIds.includes(data.id)),
    );
  }

  static getStaffStatuses(staffIds: number[]): (staff: Loadable<StaffEntity>[]) => LoadableStatus[] {
    return createSelector(
      [CommonClientRequestsState.getStaffLoadables(staffIds)],
      staff => staff.map(({ status }) => status),
    );
  }

  // don't judge the name please
  static getStaffs(staffIds: number[]): (staff: Loadable<StaffEntity>[]) => StaffEntity[] {
    return createSelector(
      [CommonClientRequestsState.getStaffLoadables(staffIds)],
      staff => LoadableHelper.getData(staff),
    );
  }

  static getStaffLoadable(staffId: number): (staff: Loadable<StaffEntity>[]) => Loadable<StaffEntity> {
    return createSelector(
      [CommonClientRequestsState.getAllStaffLoadables],
      staff => staff.find(({ data }) => data.id === staffId),
    );
  }

  static getStaffStatus(staffId: number): (loadable: Loadable<StaffEntity>) => LoadableStatus {
    return createSelector(
      [CommonClientRequestsState.getStaffLoadable(staffId)],
      loadable => loadable?.status,
    );
  }

  static getStaff(staffId: number): (loadable: Loadable<StaffEntity>) => StaffEntity {
    return createSelector(
      [CommonClientRequestsState.getStaffLoadable(staffId)],
      loadable => loadable?.data,
    );
  }

  //#endregion

  //#region Service Lines

  @Selector()
  static getAllServiceLinesLoadables({ serviceLines }: CommonClientRequestsStateModel): Loadable<BaseJobServiceLine>[] {
    return serviceLines;
  }

  @Selector([CommonClientRequestsState.getAllServiceLinesLoadables])
  static getAllServiceLinesStatuses(serviceLines: Loadable<BaseJobServiceLine>[]): LoadableStatus[] {
    return LoadableHelper.getStatuses(serviceLines);
  }

  @Selector([CommonClientRequestsState.getAllServiceLinesStatuses])
  static getAllServiceLinesStatus(statuses: LoadableStatus[]): LoadableStatus {
    return LoadableHelper.coalesceStatus(statuses);
  }

  @Selector([CommonClientRequestsState.getAllServiceLinesLoadables])
  static getAllServiceLines(serviceLines: Loadable<BaseJobServiceLine>[]): BaseJobServiceLine[] {
    return LoadableHelper.getData(serviceLines).filter(Boolean);
  }

  static getServiceLinesLoadables(
    serviceLineIds: number[],
  ): (serviceLines: Loadable<BaseJobServiceLine>[]) => Loadable<BaseJobServiceLine>[] {
    return createSelector(
      [CommonClientRequestsState.getAllServiceLinesLoadables],
      serviceLines => serviceLines.filter(({ data }) => serviceLineIds.includes(data.id)),
    );
  }

  static getServiceLinesLoadablesByJobId(jobId: number): (serviceLines: Loadable<BaseJobServiceLine>[]) => Loadable<BaseJobServiceLine>[] {
    return createSelector(
      [CommonClientRequestsState.getAllServiceLinesLoadables],
      serviceLines => serviceLines.filter(({ data }) => data?.jobId === jobId),
    );
  }

  static getServiceLinesStatuses(serviceLineIds: number[]): (serviceLines: Loadable<BaseJobServiceLine>[]) => LoadableStatus[] {
    return createSelector(
      [CommonClientRequestsState.getServiceLinesLoadables(serviceLineIds)],
      serviceLines => serviceLines.map(({ status }) => status),
    );
  }

  static getServiceLines(serviceLineIds: number[]): (serviceLines: Loadable<BaseJobServiceLine>[]) => BaseJobServiceLine[] {
    return createSelector(
      [CommonClientRequestsState.getServiceLinesLoadables(serviceLineIds)],
      serviceLines => LoadableHelper.getData(serviceLines),
    );
  }

  static getServiceLineLoadable(serviceLineId: number): (serviceLines: Loadable<BaseJobServiceLine>[]) => Loadable<BaseJobServiceLine> {
    return createSelector(
      [CommonClientRequestsState.getAllServiceLinesLoadables],
      serviceLines => serviceLines.find(({ data }) => data.id === serviceLineId),
    );
  }

  static getServiceLineStatus(serviceLineId: number): (loadable: Loadable<BaseJobServiceLine>) => LoadableStatus {
    return createSelector(
      [CommonClientRequestsState.getServiceLineLoadable(serviceLineId)],
      loadable => loadable?.status,
    );
  }

  static getServiceLine(serviceLineId: number): (loadable: Loadable<BaseJobServiceLine>) => BaseJobServiceLine {
    return createSelector(
      [CommonClientRequestsState.getServiceLineLoadable(serviceLineId)],
      loadable => loadable?.data,
    );
  }

  //#endregion

  //#region Deliverables

  @Selector()
  static getAllDeliverablesLoadables({ deliverables }: CommonClientRequestsStateModel): Loadable<DeliverableModel>[] {
    return deliverables;
  }

  @Selector([CommonClientRequestsState.getAllDeliverablesLoadables])
  static getAllDeliverablesStatuses(deliverables: Loadable<DeliverableModel>[]): LoadableStatus[] {
    return LoadableHelper.getStatuses(deliverables);
  }

  @Selector([CommonClientRequestsState.getAllDeliverablesStatuses])
  static getAllDeliverablesStatus(statuses: LoadableStatus[]): LoadableStatus {
    return LoadableHelper.coalesceStatus(statuses);
  }

  @Selector([CommonClientRequestsState.getAllDeliverablesLoadables])
  static getAllDeliverables(deliverables: Loadable<DeliverableModel>[]): DeliverableModel[] {
    return LoadableHelper.getData(deliverables).filter(Boolean);
  }

  @Selector([CommonClientRequestsState.getAllClientRequestDetails])
  static getAllRestrictedItems(clientRequestDetails: ClientRequestDetails[]): RequestDocument[] {
    return clientRequestDetails
      .map((record) => {
        return record.sections
          .map((section) => {
            return (section.items as RequestDocument[]).map((item) => {
              const isRestricted = isNil(item.restricted) ? item.files.some((f) => (f.file as FileModel).isMasked) : item.restricted;
              return isRestricted ? item : null;
            });
          })
          .flat();
      })
      .flat()
      .filter(Boolean);
  }

  static getDeliverablesLoadables(
    targetIdentities: TypedIdentity<DeliverableIdentityType>[],
  ): (deliverables: Loadable<DeliverableModel>[]) => Loadable<DeliverableModel>[] {
    return createSelector(
      [CommonClientRequestsState.getAllDeliverablesLoadables],
      deliverables => deliverables.filter(({ identity }) => targetIdentities.includes(identity as TypedIdentity<DeliverableIdentityType>)),
    );
  }

  static getDeliverablesStatuses(
    targetIdentities: TypedIdentity<DeliverableIdentityType>[],
  ): (deliverables: Loadable<DeliverableModel>[]) => LoadableStatus[] {
    return createSelector(
      [CommonClientRequestsState.getDeliverablesLoadables(targetIdentities)],
      deliverables => LoadableHelper.getStatuses(deliverables),
    );
  }

  static getDeliverables(
    targetIdentities: TypedIdentity<DeliverableIdentityType>[],
  ): (deliverables: Loadable<DeliverableModel>[]) => DeliverableModel[] {
    return createSelector(
      [CommonClientRequestsState.getDeliverablesLoadables(targetIdentities)],
      deliverables => LoadableHelper.getData(deliverables),
    );
  }

  static getDeliverableLoadable(
    targetIdentity: TypedIdentity<DeliverableIdentityType>,
  ): (deliverables: Loadable<DeliverableModel>[]) => Loadable<DeliverableModel> {
    return createSelector(
      [CommonClientRequestsState.getAllDeliverablesLoadables],
      deliverables => deliverables.find(({ identity }) => identity === targetIdentity),
    );
  }

  static getDeliverableStatus(
    targetIdentity: TypedIdentity<DeliverableIdentityType>,
  ): (loadable: Loadable<DeliverableModel>) => LoadableStatus {
    return createSelector(
      [CommonClientRequestsState.getDeliverableLoadable(targetIdentity)],
      loadable => loadable?.status,
    );
  }

  static getDeliverable(
    targetIdentity: TypedIdentity<DeliverableIdentityType>,
  ): (loadable: Loadable<DeliverableModel>) => DeliverableModel {
    return createSelector(
      [CommonClientRequestsState.getDeliverableLoadable(targetIdentity)],
      loadable => loadable?.data,
    );
  }

  static getDeliverablesInGroup(
    group: Loadable<ClientRequestGroupModel>,
  ): (deliverables: Loadable<DeliverableModel>[]) => Loadable<DeliverableModel>[] {
    const groupIdentity: TypedIdentity<ClientRequestGroupType> = group.identity as TypedIdentity<ClientRequestGroupType>
      || ClientRequestIdentityHelper.getGroupIdentity(group.data);

    return createSelector(
      [CommonClientRequestsState.getAllDeliverablesLoadables],
      deliverables => deliverables.filter(({ data }) => CommonClientRequestsState.doesDeliverableBelongToGroup(data, groupIdentity)),
    );
  }

  static doesDeliverableBelongToGroup(deliverable: DeliverableModel, groupIdentity: TypedIdentity<ClientRequestGroupType>): boolean {
    if (deliverable == undefined) {
      return false;
    }

    const parsedGroupIdentity = ClientRequestIdentityHelper.parseGroupIdentity(groupIdentity);

    return parsedGroupIdentity.type === ClientRequestGroupType.JOB && deliverable.jobId === parsedGroupIdentity.id;
  }

  //#endregion

  //#region Files

  @Selector()
  static getAllFilesLoadables({ files }: CommonClientRequestsStateModel): Loadable<BaseFile>[] {
    return files;
  }

  @Selector([CommonClientRequestsState.getAllFilesLoadables])
  static getAllFilesStatuses(files: Loadable<BaseFile>[]): LoadableStatus[] {
    return LoadableHelper.getStatuses(files);
  }

  @Selector([CommonClientRequestsState.getAllFilesStatuses])
  static getAllFilesStatus(statuses: LoadableStatus[]): LoadableStatus {
    return LoadableHelper.coalesceStatus(statuses);
  }

  @Selector([CommonClientRequestsState.getAllFilesLoadables])
  static getAllFiles(files: Loadable<BaseFile>[]): BaseFile[] {
    return LoadableHelper.getData(files).filter(Boolean);
  }

  static getFilesLoadables(
    fileIds: number[],
  ): (files: Loadable<BaseFile>[]) => Loadable<BaseFile>[] {
    return createSelector(
      [CommonClientRequestsState.getAllFilesLoadables],
      files => files.filter(({ data }) => fileIds.includes(data?.id)),
    );
  }

  static getFilesStatuses(fileIds: number[]): (files: Loadable<BaseFile>[]) => LoadableStatus[] {
    return createSelector(
      [CommonClientRequestsState.getFilesLoadables(fileIds)],
      files => files.map(({ status }) => status),
    );
  }

  static getFiles(fileIds: number[]): (files: Loadable<BaseFile>[]) => BaseFile[] {
    return createSelector(
      [CommonClientRequestsState.getFilesLoadables(fileIds)],
      files => LoadableHelper.getData(files),
    );
  }

  static getFileLoadable(fileId: number): (files: Loadable<BaseFile>[]) => Loadable<BaseFile> {
    return createSelector(
      [CommonClientRequestsState.getAllFilesLoadables],
      files => files.find(({ data }) => data.id === fileId),
    );
  }

  static getFileStatus(fileId: number): (loadable: Loadable<BaseFile>) => LoadableStatus {
    return createSelector(
      [CommonClientRequestsState.getFileLoadable(fileId)],
      loadable => loadable?.status,
    );
  }

  static getFile(fileId: number): (loadable: Loadable<BaseFile>) => BaseFile {
    return createSelector(
      [CommonClientRequestsState.getFileLoadable(fileId)],
      loadable => loadable?.data,
    );
  }

  //#endregion

  //#endregion

  constructor(
    private readonly store: Store,
    private readonly logger: Logger,
    private readonly clientRequestApiService: ClientRequestApiService,
    private readonly contactApiService: ContactApiService,
    private readonly staffApiService: StaffApiService,
    private readonly clientApiService: ClientApiService,
    private readonly jobApiService: JobApiService,
    private readonly fileApiService: FileApiService,
    private readonly authStateAdaptor: AuthStateAdaptorService,
    private readonly authService: AuthService,
  ) {}

  //#region Actions

  //#region Client Request Groups

  /**
   * Get all relevant client request groups.
   * @param patchState
   * @param loadStrategy
   */
  @Action([GetAllClientRequestGroups])
  getAllClientRequestGroups(
    { patchState }: StateContext<CommonClientRequestsStateModel>,
    { loadStrategy }: GetAllClientRequestGroups,
  ): Observable<ClientRequestGroupModel[] | LoadableStatus> {
    
    const allClientRequestGroups = this.store.selectSnapshot(CommonClientRequestsState.getAllClientRequestGroupsLoadables);
    const clientRequestGroupsLoadingState = getLoadableArrayLoadingState(
      allClientRequestGroups,
      loadStrategy,
      ClientRequestIdentityHelper.getGroupIdentity,
    );

    if (clientRequestGroupsLoadingState == undefined) {
      this.logger.debug('CommonClientRequestsState : GetAllClientRequestGroups : Skipping request');
      return this.store.select(CommonClientRequestsState.getAllClientRequestGroupsStatus).pipe(
        isLoaded(true),
        take(1),
      );
    }

    patchState({ groups: clientRequestGroupsLoadingState.loadableArray });

    const clientGroupIdsToRefresh = ClientRequestIdentityHelper.parseGroupIdentities(clientRequestGroupsLoadingState.identitiesToRequest);
    const includeUngrouped = clientGroupIdsToRefresh.ungrouped ? { includeUngrouped: true } : {};

    return this.getAllContactIds().pipe(
      take(1),
      switchMap((contactIds) =>
        this.clientRequestApiService.getClientRequestGroups({
          contactIds,
          ...(loadStrategy === LoadableArrayLoadStrategy.LOAD
            ? {
                jobIds: clientGroupIdsToRefresh.jobIds,
                engagementIds: clientGroupIdsToRefresh.engagementIds,
                ...includeUngrouped,
              }
            : {}),
        })
      ),
      setLoadableArrayInState(
        patchState,
        'groups',
        allClientRequestGroups,
        ClientRequestIdentityHelper.getGroupIdentity
      )
    );
  }

  //#endregion

  //#region Client Requests

  /**
   * Get all relevant client requests.
   * @param patchState
   * @param loadStrategy
   */
  @Action([GetAllClientRequests])
  getAllClientRequests(
    { patchState }: StateContext<CommonClientRequestsStateModel>,
    { loadStrategy }: GetAllClientRequests,
  ): Observable<ClientRequestModel[] | LoadableStatus> {
    const allClientRequests = this.store.selectSnapshot(CommonClientRequestsState.getAllClientRequestsLoadables);
    const clientRequestsLoadingState = getLoadableArrayLoadingState(
      allClientRequests,
      loadStrategy,
      ClientRequestIdentityHelper.getRequestIdentity,
    );

    if (clientRequestsLoadingState == undefined) {
      this.logger.debug('CommonClientRequestsState : GetAllClientRequests : Skipping request');
      return this.store.select(CommonClientRequestsState.getAllClientRequestGroupsStatus).pipe(
        isLoaded(true),
        take(1),
      );
    }

    patchState({ requests: clientRequestsLoadingState.loadableArray });

    return this.getAllContactIds().pipe(
      take(1),
      switchMap(contactIds => this.clientRequestApiService.getClientRequests({
        contactIds,
        // limit the scope if forceRefresh is false and some entities have already been requested
        clientRequestIds: loadStrategy === LoadableArrayLoadStrategy.LOAD
          ? clientRequestsLoadingState.identitiesToRequest.refresh.map(identity => {
            const parsedIdentity = ClientRequestIdentityHelper.parseRequestIdentity(identity);
            return parsedIdentity.id;
          })
          : undefined,
      })),
      setLoadableArrayInState(
        patchState,
        'requests',
        allClientRequests,
        ClientRequestIdentityHelper.getRequestIdentity,
      ),
    );
  }

  /**
   * Get a specified subset of relevant client requests.
   * @param patchState
   * @param requestIds
   * @param loadStrategy
   */
  @Action([GetClientRequests])
  getClientRequests(
    { patchState }: StateContext<CommonClientRequestsStateModel>,
    { requestIds, loadStrategy, requestItemAssigneeIds }: GetClientRequests,
  ): Observable<ClientRequestModel[] | LoadableStatus> {
    if (isEmpty(requestIds)) {
      this.logger.debug('CommonClientRequestsState : GetClientRequests : No request IDs provided');
      return;
    }

    // determine if the request should be performed and, if so, set the affected loadables to loading or add a new loadable if empty
    const allClientRequests = this.store.selectSnapshot(CommonClientRequestsState.getAllClientRequestsLoadables);
    const clientRequestsLoadingState = getLoadableArrayLoadingState(
      allClientRequests,
      loadStrategy,
      ClientRequestIdentityHelper.getRequestIdentity,
      requestIds.map(id => ClientRequestIdentityHelper.getRequestIdentity({ id } as ClientRequestModel)),
    );

    // do not perform request
    if (clientRequestsLoadingState == undefined) {
      this.logger.debug('CommonClientRequestsState : GetClientRequests : Skipping request');
      return this.store.select(CommonClientRequestsState.getClientRequestsStatus(requestIds)).pipe(
        isLoaded(true),
        take(1),
      );
    }

    patchState({ requests: clientRequestsLoadingState.loadableArray });

    const clientRequestIds = flattenIdentitiesToRequest(clientRequestsLoadingState.identitiesToRequest);

    return this.getAllContactIds().pipe(
      take(1),
      switchMap(contactIds => this.clientRequestApiService.getClientRequests({
        contactIds,
        clientRequestIds: clientRequestIds.map(identity => ClientRequestIdentityHelper.parseRequestIdentity(identity).id),
        clientRequestItemAssigneeIds: requestItemAssigneeIds,
      })),
      setLoadableArrayInState(
        patchState,
        'requests',
        allClientRequests,
        ClientRequestIdentityHelper.getRequestIdentity,
        { requestedIdentities: clientRequestIds },
      ),
    );
  }

  //#endregion

  //#region Details

  @Action([GetClientRequestDetails])
  getClientRequestDetails(
    { patchState, dispatch }: StateContext<CommonClientRequestsStateModel>,
    { requestId, loadStrategy, requestItemIds }: GetClientRequestDetails,
  ): Observable<ClientRequestDetails[] | LoadableStatus> {
    if (requestId == undefined) {
      this.logger.debug('CommonClientRequestsState : GetClientRequestDetails : No request ID provided');
      return;
    }

    // determine if the request should be performed and, if so, set the affected loadables to loading or add a new loadable if empty
    const allClientRequestDetails = this.store.selectSnapshot(CommonClientRequestsState.getAllClientRequestDetailsLoadables);
    const clientRequestsLoadingState = getLoadableArrayLoadingState(
      allClientRequestDetails,
      loadStrategy,
      ClientRequestIdentityHelper.getDetailsIdentity,
      [ClientRequestIdentityHelper.getDetailsIdentity({ clientRequestId: requestId } as ClientRequestDetails)],
    );

    // do not perform request
    if (clientRequestsLoadingState == undefined) {
      this.logger.debug('CommonClientRequestsState : GetClientRequestDetails : Skipping request');
      return this.store.select(CommonClientRequestsState.getClientRequestDetailsStatus(requestId)).pipe(
        isLoaded(true),
        take(1),
      );
    }

    // set to loading
    patchState({ details: clientRequestsLoadingState.loadableArray });

    // get supporting request data
    const clientRequestIds = flattenIdentitiesToRequest(clientRequestsLoadingState.identitiesToRequest);
    const clientRequestId = ClientRequestIdentityHelper.parseDetailsIdentity(clientRequestIds[0]);

    // perform request and set in state
    return this.getAllContactIds().pipe(
      withLatestFrom(this.authService.getUser()),
      switchMap(([contactIds, user]) => this.clientRequestApiService.getClientRequestDetails(
        clientRequestId.id,
        contactIds,
        requestItemIds,
        user,
      )),
      tap(details => {
        // Dispatch success action with details payload
        dispatch(new GetClientRequestDetailsSuccess(requestId, details));
      }),
      map(details => [details]),
      setLoadableArrayInState(
        patchState,
        'details',
        allClientRequestDetails,
        ClientRequestIdentityHelper.getDetailsIdentity,
        { requestedIdentities: clientRequestIds },
      ),
    );
  }

  @Action([GetQuestionnaireResponses])
  getQuestionnaireResponses(
    { patchState }: StateContext<CommonClientRequestsStateModel>,
    { clientRequestId }: GetQuestionnaireResponses,
  ): Observable<QuestionnaireResponse[]> {
    if (clientRequestId == undefined) {
      this.logger.debug('CommonClientRequestsState : GetQuestionnaireResponses : No client request ID provided');
      return;
    }

    // Set loading state
    if(this.store.selectSnapshot(CommonClientRequestsState.getQuestionnaireResponsesStatus) !== LoadableStatus.LOADED) {
      patchState({ questionnaireResponses: { status: LoadableStatus.LOADING } });
    }

    // perform request and set in state
    return this.clientRequestApiService.getQuestionnaireResponses(clientRequestId).pipe(
      tap(questionnaireResponses => {
        patchState({
          questionnaireResponses: {
            data: questionnaireResponses,
            status: LoadableStatus.LOADED,
          }
        });
      }),
    );
  }

  @Action([InsertCategorizedFilesIntoClientRequest])
  insertFilesIntoClientRequestDocuments(
    { patchState }: StateContext<CommonClientRequestsStateModel>,
    { clientRequestId, documentSubmissionFiles }: InsertCategorizedFilesIntoClientRequest,
  ): void {
    const clientRequestDetailsLoadable = this.store.selectSnapshot(
      CommonClientRequestsState.getClientRequestDetailsLoadable(clientRequestId),
    );
    if (!clientRequestDetailsLoadable?.data) {
      this.logger.warn('CommonClientRequestsState : InsertFilesIntoClientRequestDocuments : No client request details found.');
      return;
    }

    const updatedSections: RequestSection[] = documentSubmissionFiles.reduce(
      (acc, documentSubmissionFile) => {
        const matchingSection = acc.find(section =>
          section.items.find(({ id }) => id === documentSubmissionFile.documentId),
        );

        // should never happen, but being safe
        if (!matchingSection) {
          this.logger.warn('CommonClientRequestsState : InsertFilesIntoClientRequestDocuments : No matching section found.');
          return acc;
        }

        const requestItems = matchingSection.items;
        const matchingRequestItem = requestItems.find(({ id }) => id === documentSubmissionFile.documentId);

        const updatedRequestItems = replaceArrayItem(
          requestItems,
          requestItems.indexOf(matchingRequestItem),
          {
            ...matchingRequestItem,
            files: matchingRequestItem.files.concat(documentSubmissionFile)
          },
        );

        return replaceArrayItem(
          acc,
          acc.indexOf(matchingSection),
          { ...matchingSection, items: updatedRequestItems },
        );
      },
      clientRequestDetailsLoadable.data.sections,
    );

    const allClientRequestDetails = this.store.selectSnapshot(CommonClientRequestsState.getAllClientRequestDetailsLoadables);
    const clientRequestDetailsIndex = allClientRequestDetails.findIndex(({ data }) => data?.clientRequestId === clientRequestId);
    const updatedClientRequestDetails = replaceArrayItem(
      allClientRequestDetails,
      clientRequestDetailsIndex,
      {
        ...clientRequestDetailsLoadable,
        data: { ...clientRequestDetailsLoadable.data, sections: updatedSections },
      },
    );

    patchState({ details: updatedClientRequestDetails });
  }

  @Action([InsertUncategorizedFilesIntoClientRequest])
  insertFilesIntoClientRequestUncategorized(
    { patchState }: StateContext<CommonClientRequestsStateModel>,
    { clientRequestId, requestSubmissionFiles }: InsertUncategorizedFilesIntoClientRequest,
  ): void {
    const clientRequestDetailsLoadable = this.store.selectSnapshot(
      CommonClientRequestsState.getClientRequestDetailsLoadable(clientRequestId),
    );
    if (!clientRequestDetailsLoadable?.data) {
      this.logger.warn('CommonClientRequestsState : InsertFilesIntoClientRequestUncategorized : No client request details found.');
      return;
    }

    const updatedFiles = clientRequestDetailsLoadable.data.files.concat(requestSubmissionFiles);

    const allClientRequestDetails = this.store.selectSnapshot(CommonClientRequestsState.getAllClientRequestDetailsLoadables);
    const clientRequestDetailsIndex = allClientRequestDetails.findIndex(({ data }) => data?.clientRequestId === clientRequestId);
    const updatedClientRequestDetails = replaceArrayItem(
      allClientRequestDetails,
      clientRequestDetailsIndex,
      {
        ...clientRequestDetailsLoadable,
        data: { ...clientRequestDetailsLoadable.data, files: updatedFiles },
      },
    );

    patchState({ details: updatedClientRequestDetails });
  }

  //#endregion

  //#region Metadata

  @Action([GetClientRequestMetadata])
  getSelectedClientRequestMetadata(
    { patchState }: StateContext<CommonClientRequestsStateModel>,
    { requestId, loadStrategy }: GetClientRequestMetadata,
  ): Observable<ClientRequestMetadataModel[] | LoadableStatus> {
    if (requestId == undefined) {
      this.logger.debug('CommonClientRequestsState : GetSelectedClientRequestMetadata : No request ID provided');
      return;
    }

    // determine if the request should be performed and, if so, set the affected loadables to loading or add a new loadable if empty
    const allClientRequestMetadata = this.store.selectSnapshot(CommonClientRequestsState.getAllClientRequestMetadataLoadables);
    const clientRequestsLoadingState = getLoadableArrayLoadingState(
      allClientRequestMetadata,
      loadStrategy,
      ClientRequestIdentityHelper.getMetadataIdentity,
      [ClientRequestIdentityHelper.getMetadataIdentity({ clientRequestId: requestId } as ClientRequestMetadataModel)],
    );

    // do not perform request
    if (clientRequestsLoadingState == undefined) {
      this.logger.debug('CommonClientRequestsState : GetClientRequestMetadata : Skipping request');
      return this.store.select(CommonClientRequestsState.getClientRequestMetadataStatus(requestId)).pipe(
        isLoaded(true),
        take(1),
      );
    }

    const strategy$ = this._getDocumentRequestMetadata(requestId);
    if (!strategy$) {
      return throwError(() => new Error('Could not retrieve client request metadata. Unsupported client request type.'));
    }

    // set to loading
    patchState({ metadata: clientRequestsLoadingState.loadableArray });

    // perform request and set in state
    return strategy$.pipe(
      map(metadata => [metadata]),
      setLoadableArrayInState(
        patchState,
        'metadata',
        allClientRequestMetadata,
        ClientRequestIdentityHelper.getMetadataIdentity,
        { requestedIdentities: flattenIdentitiesToRequest(clientRequestsLoadingState.identitiesToRequest) },
      ),
    );
  }
  private _getDocumentRequestMetadata(requestId: number): Observable<DocumentRequestMetadataModel> | void {
    return this.getAllContactIds().pipe(
      withLatestFrom(this.authService.getUser()),
      switchMap(([contactIds, user]) => {
        // Extract fileIds from already-loaded details to include in metadata query
        const details = this.store.selectSnapshot(
          CommonClientRequestsState.getClientRequestDetails(requestId)
        );

        // Collect all fileIds from item-linked files in details.sections[].items[].files[]
        const itemLinkedFileIds: number[] = [];
        if (details?.sections) {
          details.sections.forEach(section => {
            section.items.forEach(item => {
              if (item.files) {
                item.files.forEach(file => {
                  itemLinkedFileIds.push(file.fileId);
                });
              }
            });
          });
        }

        // Pass itemLinkedFileIds to the API call with OR logic
        // OR logic ensures item-linked files are included regardless of permission checks
        return this.clientRequestApiService.getClientRequestSubmissionFiles(
          requestId,
          user,
          contactIds,
          itemLinkedFileIds.length > 0 ? itemLinkedFileIds : undefined,
          'OR' // Use OR logic for item-linked files
        );
      }),
      map((files) => ({ clientRequestId: requestId, files }) as DocumentRequestMetadataModel),
    );
  }

  @Action([AddClientRequestMetadataFiles])
  addClientRequestMetadataFiles(
    { patchState }: StateContext<CommonClientRequestsStateModel>,
    { requestId, fileIds }: AddClientRequestMetadataFiles,
  ): Observable<RequestSubmissionFile[]> {
    return this.getAllContactIds().pipe(
      withLatestFrom(this.authService.getUser()),
      switchMap(([contactIds, user]) => this.clientRequestApiService.getClientRequestSubmissionFiles(requestId, user, contactIds, fileIds)),
      tap(submissions => {
        const allClientRequestMetadata = this.store.selectSnapshot(CommonClientRequestsState.getAllClientRequestMetadataLoadables);
        const clientRequestMetadataIndex = allClientRequestMetadata.findIndex(({ data }) => data?.clientRequestId === requestId);
        const clientRequestMetadataLoadable = allClientRequestMetadata[clientRequestMetadataIndex];

        const updatedFiles = clientRequestMetadataLoadable.data.files.concat(submissions);
        const updatedClientRequestMetadata = replaceArrayItem(
          allClientRequestMetadata,
          clientRequestMetadataIndex,
          {
            ...clientRequestMetadataLoadable,
            data: { ...clientRequestMetadataLoadable.data, files: updatedFiles },
          },
        );

        patchState({ metadata: updatedClientRequestMetadata });
      }),
    );
  }

  @Action([RemoveClientRequestFiles])
  removeClientRequestFiles(
    { patchState, dispatch }: StateContext<CommonClientRequestsStateModel>,
    { clientRequestId, fileIds }: RemoveClientRequestFiles,
  ): Observable<unknown> {
    return this.clientRequestApiService.removeClientRequestFiles({ clientRequestId, fileIds }).pipe(
      tap(_ => {
        // remove files from metadata
        const allClientRequestMetadata = this.store.selectSnapshot(CommonClientRequestsState.getAllClientRequestMetadataLoadables);
        const clientRequestMetadataIndex = allClientRequestMetadata.findIndex(({ data }) => data?.clientRequestId === clientRequestId);
        const clientRequestMetadataLoadable = allClientRequestMetadata[clientRequestMetadataIndex];

        const updatedMetadataFiles = clientRequestMetadataLoadable.data.files.filter(({ id }) => !fileIds.includes(id));
        const updatedClientRequestMetadata = replaceArrayItem(
          allClientRequestMetadata,
          clientRequestMetadataIndex,
          {
            ...clientRequestMetadataLoadable,
            data: { ...clientRequestMetadataLoadable.data, files: updatedMetadataFiles },
          },
        );

        patchState({ metadata: updatedClientRequestMetadata });

        // remove files from details (documents)
        this.alterFilesInClientRequestDocuments(patchState, clientRequestId, fileIds, true);
      }),
      switchMap(_ => dispatch(new RemoveClientRequestFilesSuccess())),
    );
  }



  @Action([UnCategorizeFilesFromClientRequestDocuments])
  unCategorizeFilesFromClientRequestDocuments(
    { patchState }: StateContext<CommonClientRequestsStateModel>,
    { clientRequestId, categorizedFilesToRemove }: UnCategorizeFilesFromClientRequestDocuments,
  ): void {
    this.alterFilesInClientRequestDocuments(patchState, clientRequestId, categorizedFilesToRemove, false);
  }

  @Action([InsertAnswersIntoRequestItems])
  insertAnswersIntoRequestItems(
    { patchState }: StateContext<CommonClientRequestsStateModel>,
    { clientRequestItemResponseList, clientRequestSubmitAnswerPayload }: InsertAnswersIntoRequestItems,
  ): void {
    const clientRequestId = clientRequestSubmitAnswerPayload.clientRequestId;
    const clientRequestDetailsLoadable = this.store.selectSnapshot(
      CommonClientRequestsState.getClientRequestDetailsLoadable(clientRequestId),
    );

    if (!clientRequestDetailsLoadable?.data) {
      this.logger.debug('CommonClientRequestsState : insertAnswersIntoRequestItems : No client request details found.');
      return;
    }

    const updatedSections: RequestSection[] = clientRequestItemResponseList.reduce(
      (acc, clientRequestItemResponse) => {
        const matchingSection = acc.find(section => section.items.find(({ id }) => id === clientRequestItemResponse.id));

        // should never happen, but being safe
        if (!matchingSection) {
          this.logger.debug('CommonClientRequestsState : insertAnswersIntoRequestItems : No matching section found.');
          return acc;
        }

        const items = matchingSection.items;
        const matchingItem = items.find(({ id }) => id === clientRequestItemResponse.id);

        const answers = clientRequestSubmitAnswerPayload.clientRequestItemAnswers;
        const matchingMetadata = answers.find(answer => answer.clientRequestItemId === clientRequestItemResponse.id);

        const updatedItems = replaceArrayItem(
          items,
          items.indexOf(matchingItem),
          {
            ...matchingItem,
            metadata: matchingMetadata.metadata,
          },
        );

        return replaceArrayItem(
          acc,
          acc.indexOf(matchingSection),
          { ...matchingSection, items: updatedItems },
        );
      },
      clientRequestDetailsLoadable.data.sections,
    );

    const allClientRequestDetails = this.store.selectSnapshot(CommonClientRequestsState.getAllClientRequestDetailsLoadables);
    const clientRequestDetailsIndex = allClientRequestDetails.findIndex(({ data }) => data?.clientRequestId === clientRequestId);

    const updatedClientRequestDetails = replaceArrayItem(
      allClientRequestDetails,
      clientRequestDetailsIndex,
      {
        ...clientRequestDetailsLoadable,
        data: {
          ...clientRequestDetailsLoadable.data,
          sections: updatedSections,
        },
      },
    );

    patchState({ details: updatedClientRequestDetails });

  }

  @Action([SetSecureLinkUserForRequestItem])
  setSecureLinkUserForRequestItem(
    { patchState }: StateContext<CommonClientRequestsStateModel>,
    { secureLinkUser, assigneesId, clientRequestId }: SetSecureLinkUserForRequestItem,
  ): void {
    const clientRequestDetailsLoadable = this.store.selectSnapshot(
      CommonClientRequestsState.getClientRequestDetailsLoadable(clientRequestId),
    );

    const updatedSections = assigneesId.reduce(
      (sections, assigneeId) => {
        const matchingSection = sections
          .find(section =>
            section.items.some(({ assignees }) =>
              assignees.some(assignee => assignee.id === assigneeId),
            ),
          );

        const items = matchingSection.items;
        const matchingItem = items.find(({ assignees }) =>
          assignees.some(assignee => assignee.id === assigneeId),
        );
        const assignees = matchingItem.assignees;
        const matchingAssignee = assignees.find(({ id }) => id === assigneeId);

        const updatedSecureLinkUsers = [
          ...matchingAssignee.secureLinkUsers,
        ];

        if (!matchingAssignee.secureLinkUsers.some(_secureLinkUser => _secureLinkUser.uuid === secureLinkUser.uuid)) {
          updatedSecureLinkUsers.push(secureLinkUser);
        }

        const updatedAssignees = replaceArrayItem(
          assignees,
          assignees.findIndex(assignee => assignee.id === assigneeId),
          {
            ...matchingAssignee,
            secureLinkUsers: updatedSecureLinkUsers,
          },
        );

        const updatedItems = replaceArrayItem(
          items,
          items.findIndex(item => item.id === matchingItem.id),
          {
            ...matchingItem,
            assignees: updatedAssignees,
          },
        );

        return replaceArrayItem(
          sections,
          sections.findIndex(section => section.id === matchingSection.id),
          { ...matchingSection, items: updatedItems },
        );
      },
      clientRequestDetailsLoadable.data.sections,
    );

    const allClientRequestDetails = this.store.selectSnapshot(CommonClientRequestsState.getAllClientRequestDetailsLoadables);
    const clientRequestDetailsIndex = allClientRequestDetails.findIndex(({ data }) => data?.clientRequestId === clientRequestId);

    const updatedClientRequestDetails = replaceArrayItem(
      allClientRequestDetails,
      clientRequestDetailsIndex,
      {
        ...clientRequestDetailsLoadable,
        data: {
          ...clientRequestDetailsLoadable.data,
          sections: updatedSections,
        },
      },
    );

    patchState({ details: updatedClientRequestDetails });
  }

  @Action([RemoveSecureLinkUserForRequestItem])
  removeSecureLinkUserForRequestItem(
    { patchState }: StateContext<CommonClientRequestsStateModel>,
    { secureLinkUser, assigneesId, clientRequestId }: RemoveSecureLinkUserForRequestItem,
  ): void {
    const clientRequestDetailsLoadable = this.store.selectSnapshot(
      CommonClientRequestsState.getClientRequestDetailsLoadable(clientRequestId),
    );

    const updatedSections = assigneesId.reduce(
      (sections, assigneeId) => {
        const matchingSection = sections
          .find(section =>
            section.items.some(({ assignees }) =>
              assignees.some(assignee => assignee.id === assigneeId),
            ),
          );

        const items = matchingSection.items;
        const matchingItem = items.find(({ assignees }) =>
          assignees.some(assignee => assignee.id === assigneeId),
        );

        const assignees = matchingItem.assignees;
        const matchingAssignee = assignees.find(({ id }) => id === assigneeId);
        const updatedSecureLinkUsers = matchingAssignee.secureLinkUsers.filter(_secureLinkUser => _secureLinkUser.emailAddress !== secureLinkUser.emailAddress);

        const updatedAssignees = replaceArrayItem(
          assignees,
          assignees.findIndex(assignee => assignee.id === assigneeId),
          {
            ...matchingAssignee,
            secureLinkUsers: updatedSecureLinkUsers,
          },
        );

        const updatedItems = replaceArrayItem(
          items,
          items.findIndex(item => item.id === matchingItem.id),
          {
            ...matchingItem,
            assignees: updatedAssignees,
          },
        );

        return replaceArrayItem(
          sections,
          sections.findIndex(section => section.id === matchingSection.id),
          { ...matchingSection, items: updatedItems },
        );
      },
      clientRequestDetailsLoadable.data.sections,
    );

    const allClientRequestDetails = this.store.selectSnapshot(CommonClientRequestsState.getAllClientRequestDetailsLoadables);
    const clientRequestDetailsIndex = allClientRequestDetails.findIndex(({ data }) => data?.clientRequestId === clientRequestId);

    const updatedClientRequestDetails = replaceArrayItem(
      allClientRequestDetails,
      clientRequestDetailsIndex,
      {
        ...clientRequestDetailsLoadable,
        data: {
          ...clientRequestDetailsLoadable.data,
          sections: updatedSections,
        },
      },
    );

    patchState({ details: updatedClientRequestDetails });
  }

  @Action([MarkDeliverableFiles])
  markDeliverableFiles(
    { dispatch }: StateContext<CommonClientRequestsStateModel>,
    { deliverableId, fileIds, action }: MarkDeliverableFiles,
  ): Observable<boolean> {
    return this.clientRequestApiService.markDeliverableFiles(deliverableId, fileIds, action);
  }

  /**
   * Common function to alter files in client request documents
   * @param patchState The patch state function from StateContext
   * @param clientRequestId The ID of the client request
   * @param categorizedFiles The files to be altered, either as an array of file IDs or as a map of document IDs to file IDs
   * @param removeFiles Whether to remove the files (true) or add them (false)
   * @param keepInLibrary Whether to keep the files in the library when removing from sections
   */
  alterFilesInClientRequestDocuments(
    patchState: (state: Partial<CommonClientRequestsStateModel>) => void,
    clientRequestId: number,
    categorizedFiles: number[] | Record<string, number[]>,
    removeFiles: boolean = true
  ): void {
    const clientRequestDetailsLoadable = this.store.selectSnapshot(
      CommonClientRequestsState.getClientRequestDetailsLoadable(clientRequestId),
    );
    if (!clientRequestDetailsLoadable?.data) {
      this.logger.debug('CommonClientRequestsState : alterFilesInClientRequestDocuments : No client request details found.');
      return;
    }

    const marshaledCategorizedFilesToRemove = Array.isArray(categorizedFiles)
      ? categorizedFiles.map(fileId => [undefined as string, [fileId]])
      : Object.entries(categorizedFiles);
    const updatedSections: RequestSection[] = marshaledCategorizedFilesToRemove.reduce(
      (acc, [documentId, fileIds]: [string, number[]]) => {
        const matchingSection = acc.find(section => section.items.find(({ id }) => id === +documentId));

        // should never happen, but being safe
        if (!matchingSection) {
          this.logger.debug('CommonClientRequestsState : alterFilesInClientRequestDocuments : No matching section found.');
          return acc;
        }

        const documents = matchingSection.items;
        const matchingDocument = documents.find(({ id }) => id === +documentId);

        const updatedFiles = matchingDocument.files.filter(({ fileId }) => !fileIds.includes(fileId));

        const updatedDocuments = replaceArrayItem(
          documents,
          documents.indexOf(matchingDocument),
          { ...matchingDocument, files: updatedFiles },
        );

        return replaceArrayItem(
          acc,
          acc.indexOf(matchingSection),
          { ...matchingSection, items: updatedDocuments },
        );
      },
      clientRequestDetailsLoadable.data.sections,
    );

    const categorizedFilesIdsToRemove = Array.isArray(categorizedFiles)
      ? categorizedFiles
      : Object.values(categorizedFiles).reduce((acc, fileIds) => acc.concat(fileIds), [] as number[]);

    let updatedFiles = clientRequestDetailsLoadable.data.files;
    // If we are removing files, get rid of them from the files list...
    if (removeFiles) {
      updatedFiles = clientRequestDetailsLoadable.data.files.filter(({ fileId }) => !categorizedFilesIdsToRemove.includes(fileId));
    }

    const allClientRequestDetails = this.store.selectSnapshot(CommonClientRequestsState.getAllClientRequestDetailsLoadables);
    const clientRequestDetailsIndex = allClientRequestDetails.findIndex(({ data }) => data?.clientRequestId === clientRequestId);

    const updatedClientRequestDetails = replaceArrayItem(
      allClientRequestDetails,
      clientRequestDetailsIndex,
      {
        ...clientRequestDetailsLoadable,
        data: {
          ...clientRequestDetailsLoadable.data,
          sections: updatedSections,
          files: updatedFiles,
        },
      },
    );

    patchState({ details: updatedClientRequestDetails });
  }

  //#endregion

  //#region Clients

  @Action([GetClients])
  getClients(
    { patchState }: StateContext<CommonClientRequestsStateModel>,
    { loadStrategy }: GetClients,
  ): Observable<BaseClient[] | LoadableStatus> {
    // determine if the request should be performed and, if so, set the affected loadables to loading or add a new loadable if empty
    const allClients = this.store.selectSnapshot(CommonClientRequestsState.getAllClientsLoadables);

    const groups = this.store.selectSnapshot(CommonClientRequestsState.getAllClientRequestGroupsLoadables);
    const clientIds = groups.map(group => group.data.job.clientId);

    const clientsLoadingState = getLoadableArrayLoadingState(
      allClients,
      loadStrategy,
      ClientRequestIdentityHelper.getClientIdentity,
      clientIds.map(id => ClientRequestIdentityHelper.getClientIdentity({ id } as BaseClient)),
    );

    // do not perform request
    if (clientsLoadingState == undefined) {
      this.logger.debug('CommonClientRequestsState : GetClients : Skipping request');
      return this.store.select(CommonClientRequestsState.getAllClientsStatus).pipe(
        isLoaded(true),
        take(1),
      );
    }

    // set to loading
    patchState({ clients: clientsLoadingState.loadableArray });

    // get supporting request data
    const clientIdsToRequest = flattenIdentitiesToRequest(clientsLoadingState.identitiesToRequest);
    const parsedClientIdsToRequest = clientIdsToRequest.map(identity => ClientRequestIdentityHelper.parseClientIdentity(identity).id);

    // perform request and set in state
    return this.clientApiService.getBaseClients(parsedClientIdsToRequest).pipe(
      setLoadableArrayInState(
        patchState,
        'clients',
        allClients,
        ClientRequestIdentityHelper.getClientIdentity,
        { requestedIdentities: clientIdsToRequest },
      ),
    );
  }

  //#endregion

  //#region Assignees

  /**
   * Get all relevant assignee details based on already loaded client requests.
   * @param patchState
   * @param loadStrategy
   */
  @Action([GetAssignees])
  getAssignees(
    { patchState }: StateContext<CommonClientRequestsStateModel>,
    { loadStrategy }: GetAssignees,
  ): Observable<BaseContact[] | LoadableStatus> {
    // determine if the request should be performed and, if so, set the affected loadables to loading or add a new loadable if empty
    const allAssignees = this.store.selectSnapshot(CommonClientRequestsState.getAllAssigneeLoadables);

    const allClientRequests = this.store.selectSnapshot(CommonClientRequestsState.getAllClientRequests);
    const contactIds = allClientRequests.reduce(
      (acc, request) => {
        // Changed to return the assignees not just for the Client Request, but also on the Documents as well...
        const contactAssignees: BaseContactClientRequestAssignee[] = request.combinedAssignees.filter(assignee => 'contactId' in assignee);
        return acc.concat(contactAssignees.map(({ contactId }) => contactId));
      },
      [] as number[],
    );

    const assigneesLoadingState = getLoadableArrayLoadingState(
      allAssignees,
      loadStrategy,
      ClientRequestIdentityHelper.getAssigneeIdentity,
      contactIds.map(id => ClientRequestIdentityHelper.getAssigneeIdentity({ id } as BaseContact)),
    );

    // do not perform request
    if (assigneesLoadingState == undefined) {
      this.logger.debug('CommonClientRequestsState : GetAssignees : Skipping request');
      return this.store.select(CommonClientRequestsState.getAllClientRequestGroupsStatus).pipe(
        isLoaded(true),
        take(1),
      );
    }

    // set to loading
    patchState({ assignees: assigneesLoadingState.loadableArray });

    // get supporting request data
    const contactIdsToRequest = flattenIdentitiesToRequest(assigneesLoadingState.identitiesToRequest);
    const parsedContactIdsToRequest = contactIdsToRequest.map(identity => ClientRequestIdentityHelper.parseAssigneeIdentity(identity).id);

    // perform request and set in state
    return this.contactApiService.getBaseContacts(parsedContactIdsToRequest).pipe(
      setLoadableArrayInState(
        patchState,
        'assignees',
        allAssignees,
        ClientRequestIdentityHelper.getAssigneeIdentity,
        { requestedIdentities: contactIdsToRequest },
      ),
    );
  }

  //#endregion

  //#region Staff

  /**
   * Get all relevant staff details based on already loaded client requests.
   * @param patchState
   * @param loadStrategy
   */
  @Action([GetStaff])
  getStaff(
    { patchState }: StateContext<CommonClientRequestsStateModel>,
    { loadStrategy }: GetStaff,
  ): Observable<StaffEntity[] | LoadableStatus> {
    // determine if the request should be performed and, if so, set the affected loadables to loading or add a new loadable if empty
    const allStaff = this.store.selectSnapshot(CommonClientRequestsState.getAllStaffLoadables);

    const allClientRequests = this.store.selectSnapshot(CommonClientRequestsState.getAllClientRequestsLoadables);
    const staffIds = allClientRequests.map(({ data }) => data.reviewerStaffId);

    const staffLoadingState = getLoadableArrayLoadingState(
      allStaff,
      loadStrategy,
      ClientRequestIdentityHelper.getStaffIdentity,
      staffIds.map(id => ClientRequestIdentityHelper.getStaffIdentity({ id } as StaffEntity)),
    );

    // do not perform request
    if (staffLoadingState == undefined) {
      this.logger.debug('CommonClientRequestsState : GetStaff : Skipping request');
      return this.store.select(CommonClientRequestsState.getAllClientRequestGroupsStatus).pipe(
        isLoaded(true),
        take(1),
      );
    }

    // set to loading
    patchState({ staff: staffLoadingState.loadableArray });

    // get supporting request data
    const staffIdsToRequest = flattenIdentitiesToRequest(staffLoadingState.identitiesToRequest);
    const parsedStaffIdsToRequest = staffIdsToRequest.map(identity => ClientRequestIdentityHelper.parseStaffIdentity(identity).id);

    // perform request and set in state
    return this.staffApiService.getStaff(parsedStaffIdsToRequest).pipe(
      setLoadableArrayInState(
        patchState,
        'staff',
        allStaff,
        ClientRequestIdentityHelper.getStaffIdentity,
        { requestedIdentities: staffIdsToRequest },
      ),
    );
  }

  //#endregion

  //#region Service Lines

  @Action([GetServiceLines])
  getServiceLines(
    { patchState }: StateContext<CommonClientRequestsStateModel>,
  ): Observable<BaseJobServiceLine[] | LoadableStatus> | void {
    const allClientRequestGroups = this.store.selectSnapshot(CommonClientRequestsState.getAllClientRequestGroups);
    const jobIds = allClientRequestGroups
      .filter(group => group?.type === ClientRequestGroupType.JOB)
      .map(({ job }: ClientRequestGroupJobModel) => job.id);

    const allServiceLines = this.store.selectSnapshot(CommonClientRequestsState.getAllServiceLinesLoadables);
    const requestedJobIds = removeDuplicates(allServiceLines.map(serviceLine => serviceLine.data?.jobId));
    if (jobIds.every(jobId => requestedJobIds.includes(jobId))) {
      this.logger.debug('CommonClientRequestsState : GetServiceLines : Skipping request');
      return this.store.select(CommonClientRequestsState.getAllServiceLinesStatus).pipe(
        isLoaded(true),
        take(1),
      );
    }

    // since we won't know the service lines we're trying to request, we can just add a placeholder loading unidentified loadable
    patchState({ serviceLines: allServiceLines.concat({ status: LoadableStatus.LOADING }) });

    return this.jobApiService.getBaseServiceLinesByJobIds(jobIds).pipe(
      setLoadableArrayInState(
        patchState,
        'serviceLines',
        allServiceLines,
        ClientRequestIdentityHelper.getServiceLineIdentity,
      ),
    );
  }

  //#endregion

  //#region Deliverables

  @Action([GetAllDeliverables])
  getAllDeliverables(
    { patchState }: StateContext<CommonClientRequestsStateModel>,
    { loadStrategy }: GetAllDeliverables,
  ): Observable<DeliverableModel[] | LoadableStatus> {
    this.logger.debug('CommonClientRequestsState : GetAllDeliverables : starting');
    // determine if the request should be performed and, if so, set the affected loadables to loading or add a new loadable if empty
    const allDeliverables = this.store.selectSnapshot(CommonClientRequestsState.getAllDeliverablesLoadables);
    const deliverablesLoadingState = getLoadableArrayLoadingState(
      allDeliverables,
      loadStrategy,
      ClientRequestIdentityHelper.getDeliverableIdentity,
    );

    this.logger.debug('CommonClientRequestsState : GetAllDeliverables : ', allDeliverables, deliverablesLoadingState);

    if (deliverablesLoadingState == undefined) {
      this.logger.debug('CommonClientRequestsState : GetAllDeliverables : Skipping request');
      return this.store.select(CommonClientRequestsState.getAllDeliverablesStatus).pipe(
        isLoaded(true),
        take(1),
      );
    }

    patchState({ deliverables: deliverablesLoadingState.loadableArray });
    const contactId = this.authStateAdaptor.getUserContactIdSnapshot();
    // get supporting request data
    const jobIds = this.store.selectSnapshot(CommonClientRequestsState.getAllClientRequestGroups)
      .filter(({ type }) => type === ClientRequestGroupType.JOB)
      .map(({ job }: ClientRequestGroupJobModel) => job.id);

    this.logger.debug('CommonClientRequestsState : GetAllDeliverables : jobs??', jobIds);

    return this.contactApiService.getUserContactIds(contactId, this.authStateAdaptor.getUserEmailSnapshot()).pipe(
      switchMap(contactIds => this.clientRequestApiService
      .getDeliverables(
        jobIds,
        contactId != null
          ? [
              contactId,
              ...contactIds,
            ]
          : []
      )
      .pipe(
        setLoadableArrayInState(
          patchState,
          'deliverables',
          allDeliverables,
          ClientRequestIdentityHelper.getDeliverableIdentity
        ))
      ));
  }

  @Action([UpdateDeliverablesFromSubscription])
  updateDeliverablesFromSubscription(
    { patchState }: StateContext<CommonClientRequestsStateModel>,
    { deliverables }: UpdateDeliverablesFromSubscription,
  ): void {
    if (isEmpty(deliverables)) {
      return;
    }

    const allDeliverables = this.store.selectSnapshot(CommonClientRequestsState.getAllDeliverablesLoadables);

    const updatedDeliverables = allDeliverables.map(loadable => {
      const updatedDeliverable = deliverables.find(d => d.id === loadable.data?.id);
      if (updatedDeliverable) {
        return {
          ...loadable,
          data: updatedDeliverable,
        };
      }
      return loadable;
    });

    patchState({ deliverables: updatedDeliverables });
  }

  //#endregion

  //#region Files

  @Action([GetFiles])
  getFiles(
    { patchState }: StateContext<CommonClientRequestsStateModel>,
    { fileIds, loadStrategy }: GetFiles,
  ): Observable<BaseFile[] | LoadableStatus> {
    if (isEmpty(fileIds)) {
      this.logger.debug('CommonClientRequestsState : GetFiles : No file ids provided');
      return;
    }

    // determine if the request should be performed and, if so, set the affected loadables to loading or add a new loadable if empty
    const allFiles = this.store.selectSnapshot(CommonClientRequestsState.getAllFilesLoadables);
    const filesLoadingState = getLoadableArrayLoadingState(
      allFiles,
      loadStrategy,
      ClientRequestIdentityHelper.getFileIdentity,
      fileIds.map(id => ClientRequestIdentityHelper.getFileIdentity({ id } as BaseFile)),
    );

    // do not perform request
    if (filesLoadingState == undefined) {
      this.logger.debug('CommonClientRequestsState : GetFiles : Skipping request');
      return this.store.select(CommonClientRequestsState.getAllFilesStatus).pipe(
        isLoaded(true),
        take(1),
      );
    }

    // set to loading
    patchState({ files: filesLoadingState.loadableArray });

    // perform request and set in state
    return this.fileApiService.getBaseFiles(fileIds).pipe(
      setLoadableArrayInState(
        patchState,
        'files',
        allFiles,
        ClientRequestIdentityHelper.getFileIdentity,
      ),
    );
  }

  //#endregion

  @Action([ResetState])
  resetState({ setState }: StateContext<CommonClientRequestsStateModel>): void {
    setState(DEFAULT_COMMON_CLIENT_REQUESTS_STATE);
  }

  //#endregion

  @Action([PatchClientRequestState])
  patchClientRequestState({ setState }: StateContext<CommonClientRequestsStateModel>, { patchedState }: PatchClientRequestState): void {
    setState(patchedState);
  }

  // TODO: UPDATE TO USE THE userContactIds IN APP STATE, ONCE IT'S DECOUPLED FROM ENGAGEMENTS
  private getAllContactIds(): Observable<number[]> {
    return this.authService.getUser().pipe(
      ignoreFalsy(),
      switchMap(user => {
        // If the user is a contact user, we can get the contact ID and email directly
        if (user instanceof ContactUser) {
          return combineLatest([
            of(user.getContact().id),
            of(user.getProviderEmail())
          ])
        }

        // If the user is a secure link user, we need to get the email based off who the user is SUPPOSED to be
        return combineLatest([
          of(user.getSecureLinkGrants().createdForContactId),
          this.contactApiService.getBaseContacts([user.getSecureLinkGrants().createdForContactId]).pipe(
            /**
             * There is no world in which this array brings back more than one contact.
             * NOTE: need to throw errors instead of processing an empty email. We could inadvertently return contacts that do not
             * potentially belong to the secure link user.
             */
            map(contacts => {
              const contactsWithUsername = (contacts || []).filter(({ username }) => !!username);
              if (contactsWithUsername.length === 0) {
                throw new Error('Could not find username for secure link user contact');
              }

              return contactsWithUsername[0].username;
            })
          )
        ])
      }),
      switchMap(([contactId, email]) => this.contactApiService.getUserContactIds(contactId, email).pipe(
        map(contactIds => removeDuplicates(contactIds.concat(contactId))),
      )),
      take(1),
    );
  }

  @Action(SetClientRequestItemNotApplicableSuccess)
  syncClientRequestItemAfterNotApplicableChange(
    { setState }: StateContext<CommonClientRequestsStateModel>,
    { requestItemId }: SetClientRequestItemNotApplicableSuccess
  ): Observable<unknown> {
    return this.clientRequestApiService.getClientRequestItem(requestItemId).pipe(
      tap(updatedItem => {
        setState(
          patch({
            details: updateItem(
              (detailsLoadable) => detailsLoadable.status === 'loaded',
              patch({
                data: patch({
                  sections: updateItem(
                    (section) => section.items.some(item => item.id === requestItemId),
                    patch({
                      items: updateItem(
                        (item) => item.id === requestItemId,
                        (item) => ({ ...item, ...(updatedItem as RequestItem) }) // only overwrite the updated fields, preserve the rest as-is - such as files.
                      )
                    })
                  )
                })
              })
            )
          })
        );
      })
    );
  }
}
